import { isNotNullOrUndefined } from '@vy/utils';
import { useFormContext } from 'react-hook-form';
import { VySelect } from '@vy/ui-design-system';
import { useChargeContext } from '../../charge-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { useAutoRefill } from '../hooks/use-auto-refill';
import {
  METER_INTERVAL_OPTIONS,
  findMeterIntervalOption,
  getMeterIntervalLabel,
  type MeterIntervalOption,
} from './meter-interval-options';

export const MeterRefillSelect: React.FC = () => {
  const { itemIndex: productIndex } = useChargeContext();
  const { watch, setValue } = useFormContext<PlanInputForm>();

  const { autoRefillTooltipContent, isAutoRefillDisabled, isAutoRefillChecked, accumulationInterval } =
    useAutoRefill();

  const replenishmentInterval = watch(`usageProducts.${productIndex}.commitment.replenishmentInterval`);
  const autoRefillEnabled = watch(`usageProducts.${productIndex}.commitment.autoRefillEnabled`);

  const effectiveEnabled = isAutoRefillChecked(autoRefillEnabled);
  const effectiveReplenishment = effectiveEnabled ? (replenishmentInterval ?? accumulationInterval) : undefined;

  const handleChange = (_event: React.SyntheticEvent, next: MeterIntervalOption | null) => {
    const nextValue = next?.value ?? null;

    if (isNotNullOrUndefined(nextValue)) {
      setValue(`usageProducts.${productIndex}.commitment.autoRefillEnabled`, true);
      setValue(`usageProducts.${productIndex}.commitment.replenishmentInterval`, nextValue);
      return;
    }

    setValue(`usageProducts.${productIndex}.commitment.autoRefillEnabled`, false);
    setValue(`usageProducts.${productIndex}.commitment.replenishmentInterval`, undefined);
  };

  const helperText =
    isAutoRefillDisabled && autoRefillTooltipContent
      ? autoRefillTooltipContent
      : 'Whether unused commitment units refill at the start of each period';

  return (
    <VySelect<MeterIntervalOption>
      label="Meter Refill"
      options={METER_INTERVAL_OPTIONS}
      getOptionLabel={getMeterIntervalLabel}
      value={findMeterIntervalOption(effectiveReplenishment ?? undefined)}
      onChange={handleChange}
      isOptionEqualToValue={(option, optionValue) => option.value === optionValue.value}
      disabled={isAutoRefillDisabled}
      helperText={helperText}
      data-vy="usage-charge-drawer-meter-refill-select"
    />
  );
};
